import WidgetErrorBoundary from "@/components/WidgetErrorBoundary";
import Droppable from "@/lib/Droppable";
import { logWidgetError } from "@/lib/errorLoggingACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import {
  DRAG_HEADER,
  widgetAcceptsBlockDrop,
  widgetAcceptsESIDDrop,
} from "@/lib/reactGridLayout/dndConstants";
import { componentsList } from "@/lib/widgetComponentsDir";
import { IActiveId, IState, IWidgetDetails } from "@/lib/widgetsLayoutDir";
import { Layout } from "react-grid-layout";
import WidgetHeader from "./WidgetHeader";

const GenerateAllWidgets = ({
  state,
  activeId,
  theme,
}: {
  state: IState;
  activeId: IActiveId;
  theme: string | undefined;
}) => {
  const { getAccessTokenSilently } = useAuth0();

  return state.layouts[state.currentBreakpoint].map((l: Layout) => {
    const widget = state.openWidgets.find((w: IWidgetDetails) => w.i === l.i);

    // LQC-1837: layouts and openWidgets can briefly disagree. Skip the orphan - throwing here
    // escapes WidgetErrorBoundary (it is inside this map) and took the whole workspace down.
    if (!widget || !componentsList[widget.component]) return null;

    const WidgetToJSX = componentsList[widget.component];

    const boxShadowTrade =
      "border-1 rounded-lg border-sky-200 shadow-[0_0_2px_#fff,inset_0_0_2px_#fff,0_0_5px_#08f,0_0_15px_#08f,0_0_30px_#08f]";
    // let boxShadowAnalytics =
    //   "border-1 rounded-sm border-green-200 shadow-[0_0_2px_#0f0,inset_0_0_2px_#0f0,0_0_5px_#0f0,0_0_15px_#0f0,0_0_30px_#0f0]";

    // Widgets that participate in DnD (accept a dropped ESID and/or Creation block)
    // get the Droppable wrapper plus the drag-aware props. Trade Tile = ESID only;
    // Swap Curve = ESID + block.
    const acceptsDrop =
      widgetAcceptsESIDDrop(widget.i) || widgetAcceptsBlockDrop(widget.i);

    // Glow border while a compatible item is being dragged.
    const shouldGlow =
      activeId.isActive &&
      ((activeId.headerDragging == DRAG_HEADER.ESID &&
        widgetAcceptsESIDDrop(l.i)) ||
        (activeId.headerDragging == DRAG_HEADER.BLOCK &&
          widgetAcceptsBlockDrop(l.i)));

    const renderWidget = () => {
      if (acceptsDrop) {
        return (
          <Droppable id={l.i}>
            <WidgetToJSX
              cryptoID={l.i}
              loadedFromPopout={false}
              esidToPass={activeId.value}
              activeId={activeId}
            />
          </Droppable>
        );
      }
      // BRING BACK once Analytics is working.
      // if (
      // widget.i.includes("Analytics")
      // activeId.headerDragging === "Details"
      // ) {
      // return (
      //   <Droppable id={l.i}>
      //     <WidgetToJSX cryptoID={l.i} />
      //   </Droppable>
      // );
      // };
      return <WidgetToJSX cryptoID={l.i} />;
    };

    return (
      <div
        key={l.i}
        id={l.i}
        className={
          l.static
            ? "static"
            : // background of widgets.
              // DO NOT DELETE FIRST LOAD / HIDDEN. Requierd to load AG Charts with the Cube UI. See requirement 'Intergrate AG Charts into the web app' for info.
              // WITH ANALYTICS //  `${widget.component == "FirstLoad" && "!hidden"} widgetSpace rounded-xl border-b-2 border-l border-r border-t border-[#B1B8BB] border-b-[#B1B8BB] bg-cubeBase-500 drop-shadow-sm dark:border-zinc-700 dark:bg-cubeBaseDark-500 dark:text-white ${activeId.isActive && activeId.headerDragging == "ESID" && l.i.includes("Tile") ? boxShadowTrade : ""} ${activeId.isActive && activeId.headerDragging == "Details" && l.i.includes("Analytics") ? boxShadowAnalytics : ""}`
              `${widget.component == "FirstLoad" && "!hidden"} widgetSpace flex flex-col rounded-xl border-b-2 border-l border-r border-t border-[#B1B8BB] border-b-[#B1B8BB] bg-cubeBase-500 drop-shadow-sm dark:border-zinc-700 dark:bg-cubeBaseDark-500 dark:text-white ${shouldGlow ? boxShadowTrade : ""}`
        } // DND style for widgets ABOVE
      >
        <WidgetHeader widget={widget} l={l} />

        {l.static ? (
          <span
            className="text"
            title="This item is static and cannot be removed or resized."
          >
            Static - {l.i}
          </span>
        ) : (
          <div // flex-1 + min-h-0 fills remaining space below the header banner at any font size.
            // main aspect of the widget body. Padding applied to sides and bottom to allow for the RGL drag handles to work.
            className={`min-h-0 flex-1 overflow-auto px-2 pb-2 ${theme != "dark" ? "minimalScrollBar" : "minimalScrollBarDark"}`}
          >
            <WidgetErrorBoundary
              widgetName={widget.component}
              onError={(error, info) =>
                logWidgetError(
                  widget.component,
                  error,
                  info,
                  getAccessTokenSilently,
                  activeId,
                )
              }
            >
              {renderWidget()}
            </WidgetErrorBoundary>
          </div>
        )}
      </div>
    );
  });
};

export default GenerateAllWidgets;
